import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdInventory,
  MdLocationOn,
  MdTimer,
  MdSearch,
  MdFilterList,
  MdInfo,
  MdWarning,
  MdTrendingUp,
  MdCalendarToday,
  MdViewModule,
  MdGridOn,
  MdBuild,
  MdClose,
  MdError,
  MdCheckCircle,
  MdPerson,
  MdVisibility,
  MdAssignment,
  MdPhone,
  MdArchive,
  MdLocalShipping
} from "react-icons/md";
import axios from 'axios';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import { tokenManager } from 'utils/tokenManager';

const AgentRequest = () => {
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to fetch and transform requests
  const fetchAndTransformRequests = async (authHeader) => {
    const response = await axios.get('https://usibras-web-backend.onrender.com/api/agent-pickup-requests', {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data && response.data.data) {
      const transformedData = response.data.data.map(request => {
        const formatPhone = (phone) => {
          if (!phone) return 'N/A';
          if (phone.startsWith('+')) return phone;
          if (phone.startsWith('233')) return `+${phone}`;
          if (phone.startsWith('0')) return `+233${phone.substring(1)}`;
          return `+233${phone}`;
        };
        
        const coordinates = request.location_lat && request.location_lng 
          ? `${request.location_lat.toFixed(4)}° N, ${Math.abs(request.location_lng).toFixed(4)}° ${request.location_lng < 0 ? 'W' : 'E'}`
          : 'N/A';
        
        return {
          id: `REQ-${String(request.id).padStart(3, '0')}`,
          raw_id: request.id,
          agent_id: request.agent_id,
          agent_contact: formatPhone(request.agent_contact),
          location: request.location_address,
          pickup_location: request.location_address,
          coordinates: coordinates,
          total_bags: request.total_stock || 0,
          price_per_bag: request.current_price_per_bag || 0,
          total_value: request.total_value || (request.total_stock * (request.current_price_per_bag || 0)),
          request_date: request.preferred_date || request.created_at,
          status: request.status,
          assigned_analyst: request.assigned_analyst,
          analyst_contact: request.analyst_contact,
          scheduled_pickup_date: request.scheduled_pickup_date,
          pickup_notes: request.pickup_notes,
          completion_notes: request.completion_notes,
          notes: request.notes,
          created_at: request.created_at,
          updated_at: request.updated_at
        };
      });
      setRequests(transformedData);
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get authorization header
        const authHeader = tokenManager.getAuthHeader();
        if (!authHeader) {
          throw new Error('Authentication token not found. Please login again.');
        }
        
        await fetchAndTransformRequests(authHeader);
        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        if (err.message.includes('Authentication token')) {
          setError('Authentication required. Please login again.');
        } else {
          setError('Error loading agent pickup requests. Please check your connection.');
        }
        setRequests([]);
        setLoading(false);
      }
    };
    
    fetchRequests();
  }, []);

  // Calculate statistics
  const calculateStats = (requests) => {
    const totalRequests = requests.length;
    const pendingRequests = requests.filter(r => r.status === 'pending').length;
    const inProgressRequests = requests.filter(r => r.status === 'in_progress').length;
    const completedRequests = requests.filter(r => r.status === 'completed').length;
    const totalBags = requests.reduce((sum, r) => sum + (r.total_bags || 0), 0);

    return {
      totalRequests,
      pendingRequests,
      inProgressRequests,
      completedRequests,
      totalBags
    };
  };

  // Filter and sort requests
  const filteredRequests = requests
    .filter(request => {
      const matchesSearch = 
        request.agent_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !selectedStatus || request.status === selectedStatus;
      const matchesAgent = !selectedAgent || request.agent_id === selectedAgent;
      return matchesSearch && matchesStatus && matchesAgent;
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(b.request_date) - new Date(a.request_date);
      if (sortBy === 'bags') return b.total_bags - a.total_bags;
      return 0;
    });

  const stats = calculateStats(filteredRequests);

  // Get unique agents for filters
  const agents = [...new Set(requests.map(r => r.agent_id))];

  // Pagination
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'pending': return 'Pending';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <UsibrasLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-5xl text-red-500">
            <MdError className="mx-auto h-16 w-16" />
          </div>
          <p className="text-xl font-semibold text-gray-700 dark:text-white">
            Oops! Something went wrong
          </p>
          <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 h-full">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      <Card extra={"w-full h-full p-4"}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Agent Pickup Requests
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage cashew pickup requests from agents
          </p>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdInventory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Requests</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.totalRequests}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-yellow-50 p-3 dark:bg-navy-700">
                <MdTimer className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.pendingRequests}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdLocalShipping className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.inProgressRequests}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.completedRequests}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdArchive className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Bags</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.totalBags}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex items-center gap-2">
              <MdSearch className="h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div className="flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <MdPerson className="h-5 w-5 text-gray-500" />
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Agents</option>
                {agents.map(agent => (
                  <option key={agent} value={agent}>{agent}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="date">Sort by Date</option>
                <option value="bags">Sort by Bags</option>
              </select>
            </div>
          </div>
        </div>

        {/* Requests Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-navy-700">
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Request ID</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Agent</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Location</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Total Bags</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Date</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.map((request) => (
                <tr key={request.id} className="border-b border-gray-200 dark:border-navy-700">
                  <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                    <div>
                      <p className="font-semibold">{request.id}</p>
                      <p className="text-xs text-gray-500">ID: {request.raw_id}</p>
                    </div>
                  </td>
                  <td className="py-4 text-sm">
                    <div>
                      <p className="font-medium text-navy-700 dark:text-white">{request.agent_id}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <MdPhone className="h-3 w-3 text-gray-400" />
                        <p className="text-gray-500 text-xs">{request.agent_contact}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          const coordMatch = request.coordinates.match(/([-\d.]+)°\s*([NS]),?\s*([-\d.]+)°\s*([EW])/);
                          if (coordMatch) {
                            const lat = coordMatch[2] === 'S' ? -parseFloat(coordMatch[1]) : parseFloat(coordMatch[1]);
                            const lng = coordMatch[4] === 'W' ? -parseFloat(coordMatch[3]) : parseFloat(coordMatch[3]);
                            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                          }
                        }}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        title="Open in Google Maps"
                      >
                        <MdLocationOn className="h-5 w-5" />
                      </button>
                      <div>
                        <p className="font-medium text-navy-700 dark:text-white">{request.location}</p>
                        <p className="text-xs text-gray-400">{request.coordinates}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdInventory className="h-4 w-4 text-green-500" />
                      <span className="font-semibold">{request.total_bags} bags</span>
                    </div>
                    {request.price_per_bag > 0 && (
                      <p className="text-xs text-gray-500 mt-1">@ GH₵{request.price_per_bag}/bag</p>
                    )}
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdCalendarToday className="h-4 w-4 text-purple-500" />
                      <span>{new Date(request.request_date).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="py-4">
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(request.status)}`}>
                      {getStatusLabel(request.status)}
                    </span>
                    {request.assigned_analyst && (
                      <p className="text-xs text-gray-500 mt-1">Analyst: {request.assigned_analyst}</p>
                    )}
                  </td>
                  <td className="py-4">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailModal(true);
                        }}
                        className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
                      >
                        <MdVisibility className="h-3 w-3" />
                        View
                      </button>
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setSelectedAction('in_progress');
                              setShowActionModal(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-2 py-1 text-xs font-medium text-white hover:bg-blue-600"
                          >
                            <MdLocalShipping className="h-3 w-3" />
                            In Progress
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setSelectedAction('completed');
                              setShowActionModal(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-green-500 px-2 py-1 text-xs font-medium text-white hover:bg-green-600"
                          >
                            <MdCheckCircle className="h-3 w-3" />
                            Completed
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setSelectedAction('cancelled');
                              setShowActionModal(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                          >
                            <MdClose className="h-3 w-3" />
                            Cancel
                          </button>
                        </>
                      )}
                      {request.status === 'in_progress' && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setSelectedAction('completed');
                              setShowActionModal(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-green-500 px-2 py-1 text-xs font-medium text-white hover:bg-green-600"
                          >
                            <MdCheckCircle className="h-3 w-3" />
                            Completed
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRequest(request);
                              setSelectedAction('cancelled');
                              setShowActionModal(true);
                            }}
                            className="inline-flex items-center gap-1 rounded-lg bg-red-500 px-2 py-1 text-xs font-medium text-white hover:bg-red-600"
                          >
                            <MdClose className="h-3 w-3" />
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedRequests.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredRequests.length)} of {filteredRequests.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>

        {/* No Results Message */}
        {filteredRequests.length === 0 && (
          <div className="mt-8 text-center">
            <MdInfo className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-lg font-medium text-gray-600 dark:text-gray-400">
              No agent pickup requests found matching your criteria
            </p>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
            onClick={() => setShowDetailModal(false)}
          />
          
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto transform rounded-xl bg-white p-3 shadow-2xl transition-all duration-300 ease-in-out dark:bg-navy-800 sm:p-6">
            <div className="mb-4 sm:mb-6 flex items-center justify-between border-b border-gray-200 pb-3 sm:pb-4 dark:border-navy-700">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="rounded-full bg-blue-100 p-1.5 sm:p-2 dark:bg-blue-900">
                  <MdVisibility className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                  Agent Request Details - {selectedRequest.id}
                </h3>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="rounded-full p-1 sm:p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-navy-700 dark:hover:text-gray-300"
              >
                <MdClose className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            {/* Request Summary */}
            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Request ID</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.id}</span>
                <span className="mt-1 block text-xs text-gray-500">ID: {selectedRequest.raw_id}</span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Agent ID</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.agent_id}</span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
                <span className={`mt-1 inline-block rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(selectedRequest.status)}`}>
                  {getStatusLabel(selectedRequest.status)}
                </span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Total Stock</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.total_bags} bags</span>
              </div>
              {selectedRequest.price_per_bag > 0 && (
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                  <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Price per Bag</span>
                  <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">GH₵{selectedRequest.price_per_bag.toLocaleString()}</span>
              </div>
              )}
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Request Date</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">
                  {new Date(selectedRequest.request_date).toLocaleDateString()}
                </span>
              </div>
              {selectedRequest.scheduled_pickup_date && (
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                  <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Scheduled Pickup</span>
                  <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">
                    {new Date(selectedRequest.scheduled_pickup_date).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>

            {/* Location Information */}
            <div className="mb-6">
              <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Location Details</h4>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                <div className="space-y-3">
                  <div>
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Address</span>
                    <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.location}</span>
                  </div>
                  <div>
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">GPS Coordinates</span>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.coordinates}</span>
                      <button
                        onClick={() => {
                          // Extract latitude and longitude from coordinates string
                          const coordMatch = selectedRequest.coordinates.match(/([-\d.]+)°\s*([NS]),?\s*([-\d.]+)°\s*([EW])/);
                          if (coordMatch) {
                            const lat = coordMatch[2] === 'S' ? -parseFloat(coordMatch[1]) : parseFloat(coordMatch[1]);
                            const lng = coordMatch[4] === 'W' ? -parseFloat(coordMatch[3]) : parseFloat(coordMatch[3]);
                            window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-600 transition-colors"
                      >
                        <MdLocationOn className="h-4 w-4" />
                        Open in Maps
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Contact Information */}
            <div className="mb-6">
              <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Agent Contact Information</h4>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-800">
                      <MdPerson className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-navy-700 dark:text-white">{selectedRequest.agent_id}</h5>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <MdPhone className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <a href={`tel:${selectedRequest.agent_contact}`} className="text-sm font-medium text-green-700 dark:text-green-300 hover:underline">
                        {selectedRequest.agent_contact}
                      </a>
                    </div>
                    <button 
                      onClick={() => window.open(`tel:${selectedRequest.agent_contact}`, '_self')}
                      className="text-xs bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors"
                    >
                      Call
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Analyst Information */}
            {selectedRequest.assigned_analyst && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Analyst Information</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Assigned Analyst</span>
                      <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.assigned_analyst}</span>
                    </div>
                    {selectedRequest.analyst_contact && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Analyst Contact</span>
                        <a href={`tel:${selectedRequest.analyst_contact}`} className="mt-1 block text-sm font-semibold text-blue-600 hover:underline dark:text-blue-400">
                          {selectedRequest.analyst_contact}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Notes Section */}
            {(selectedRequest.notes || selectedRequest.pickup_notes || selectedRequest.completion_notes) && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Notes</h4>
                <div className="space-y-3">
                  {selectedRequest.notes && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">General Notes</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedRequest.notes}</p>
                    </div>
                  )}
                  {selectedRequest.pickup_notes && (
                    <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Pickup Notes</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedRequest.pickup_notes}</p>
                    </div>
                  )}
                  {selectedRequest.completion_notes && (
                    <div className="rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
                      <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Completion Notes</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedRequest.completion_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timestamps */}
            {(selectedRequest.created_at || selectedRequest.updated_at) && (
              <div className="mb-6">
                <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">Timestamps</h4>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {selectedRequest.created_at && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Created</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">
                          {new Date(selectedRequest.created_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {selectedRequest.updated_at && (
                      <div>
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Last Updated</span>
                        <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">
                          {new Date(selectedRequest.updated_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (In Progress/Complete/Cancel) */}
      {showActionModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
            onClick={() => setShowActionModal(false)}
          />
          
          <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto transform rounded-xl bg-white p-3 shadow-2xl transition-all duration-300 ease-in-out dark:bg-navy-800 sm:p-6">
            <div className="mb-4 sm:mb-6 flex items-center justify-between border-b border-gray-200 pb-3 sm:pb-4 dark:border-navy-700">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`rounded-full p-1.5 sm:p-2 ${
                  selectedAction === 'in_progress' ? 'bg-blue-100 dark:bg-blue-900' : 
                  selectedAction === 'completed' ? 'bg-green-100 dark:bg-green-900' : 
                  'bg-red-100 dark:bg-red-900'
                }`}>
                  {selectedAction === 'in_progress' ? (
                    <MdLocalShipping className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
                  ) : selectedAction === 'completed' ? (
                    <MdCheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" />
                  ) : (
                    <MdClose className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400" />
                  )}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                  {selectedAction === 'in_progress' ? 'Mark as In Progress' : 
                   selectedAction === 'completed' ? 'Mark as Completed' : 
                   'Cancel Request'}
                </h3>
              </div>
              <button
                onClick={() => setShowActionModal(false)}
                className="rounded-full p-1 sm:p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-navy-700 dark:hover:text-gray-300"
              >
                <MdClose className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="mb-4 space-y-3">
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Request ID</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.id}</span>
                <span className="mt-1 block text-xs text-gray-500">ID: {selectedRequest.raw_id}</span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Agent</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.agent_id}</span>
                <div className="flex items-center gap-1 mt-1">
                  <MdPhone className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500">{selectedRequest.agent_contact}</span>
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Location</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.location}</span>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Total Stock</span>
                <span className="mt-1 block text-sm font-semibold text-navy-700 dark:text-white">{selectedRequest.total_bags} bags</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedAction === 'in_progress' ? 'Progress Note (Optional)' : 
                 selectedAction === 'completed' ? 'Completion Note (Optional)' : 
                 'Cancellation Reason'}
              </label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder={
                  selectedAction === 'in_progress' ? 'Enter progress note...' : 
                  selectedAction === 'completed' ? 'Enter completion note...' : 
                  'Enter cancellation reason...'
                }
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                rows="3"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => setShowActionModal(false)}
                disabled={isSubmitting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (selectedAction === 'cancelled' && !messageContent) {
                    setToast({ show: true, message: 'Please provide a cancellation reason', type: 'error' });
                    return;
                  }
                  
                  setIsSubmitting(true);
                  
                  try {
                    // Get authorization header
                    const authHeader = tokenManager.getAuthHeader();
                    if (!authHeader) {
                      throw new Error('Authentication token not found. Please login again.');
                    }

                    // Prepare payload
                    const payload = {
                      status: selectedAction,
                      notes: messageContent || null
                    };

                    // Make API call
                    const response = await axios.put(
                      `https://usibras-web-backend.onrender.com/api/agent-pickup-requests/${selectedRequest.raw_id}`,
                      payload,
                      {
                        headers: {
                          'Authorization': authHeader,
                          'Content-Type': 'application/json'
                        }
                      }
                    );

                    // Refresh the requests list FIRST
                    await fetchAndTransformRequests(authHeader);

                    const actionLabel = selectedAction === 'in_progress' ? 'marked as in progress' : 
                                       selectedAction === 'completed' ? 'marked as completed' : 'cancelled';
                    
                    // Close modal and show success message
                    setShowActionModal(false);
                    setShowDetailModal(false); // Also close detail modal if open
                    setMessageContent('');
                    
                    setToast({
                      show: true,
                      message: `Request ${actionLabel} successfully. Data refreshed.`,
                      type: 'success'
                    });
                  } catch (error) {
                    console.error('Error updating request:', error);
                    setToast({
                      show: true,
                      message: error.response?.data?.error || 'Failed to update request. Please try again.',
                      type: 'error'
                    });
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                disabled={isSubmitting || (selectedAction === 'cancelled' && !messageContent)}
                className={`relative rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  selectedAction === 'in_progress'
                    ? 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500 dark:bg-blue-600 dark:hover:bg-blue-700'
                    : selectedAction === 'completed'
                    ? 'bg-green-500 hover:bg-green-600 focus:ring-green-500 dark:bg-green-600 dark:hover:bg-green-700'
                    : 'bg-red-500 hover:bg-red-600 focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-700'
                } disabled:cursor-not-allowed disabled:bg-gray-400 dark:disabled:bg-gray-600`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  selectedAction === 'in_progress' ? 'Mark In Progress' : 
                  selectedAction === 'completed' ? 'Mark Completed' : 
                  'Cancel Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AgentRequest;

